import { useCallback, useEffect, useRef, useState } from 'react';
import { manualApi, manualError } from '@/lib/manual';
import { uploadVideoInParts, uploadErrorMessage, MAX_VIDEO_BYTES } from '@/lib/upload';

const uuid = () => crypto.randomUUID().replaceAll('-', '');

export function useManualClips(userId) {
  const [library, setLibrary] = useState({ sources: [], saved_videos: [] });
  const [source, setSource] = useState(null), [exports, setExports] = useState([]), [result, setResult] = useState(null);
  const [busy, setBusy] = useState('loading'), [error, setError] = useState(''), [upload, setUpload] = useState(null);
  const alive = useRef(true), operation = useRef(false), controller = useRef(null), file = useRef(null), session = useRef(null);
  const selected = useRef(null);
  const storageKey = `cg_manual_source:${userId}`;
  const remember = useCallback((data) => {
    selected.current = data.id; setSource(data); localStorage.setItem(storageKey, data.id);
  }, [storageKey]);
  const refresh = useCallback(async () => {
    const [videos, jobs] = await Promise.all([manualApi.library(), manualApi.exports()]);
    if (alive.current) { setLibrary(videos); setExports(jobs); }
    return videos;
  }, []);
  useEffect(() => {
    alive.current = true;
    refresh().then((videos) => {
      if (!alive.current || selected.current) return;
      const previous = videos.sources.find((item) => item.id === localStorage.getItem(storageKey));
      if (previous) remember(previous);
    }).catch((failure) => { if (alive.current) setError(manualError(failure)); })
      .finally(() => { if (alive.current) setBusy(''); });
    return () => { alive.current = false; controller.current?.abort(); };
  }, [refresh, remember, storageKey]);
  useEffect(() => {
    if (!source || ['ready', 'failed'].includes(source.status)) return;
    let cancelled = false, timer;
    const poll = async () => {
      try {
        const next = await manualApi.source(source.id);
        if (!cancelled) { setSource(next); setLibrary((old) => ({ ...old, sources: old.sources.map((item) => item.id === next.id ? next : item) })); setError(''); }
      } catch (failure) { if (!cancelled) setError(manualError(failure)); }
      if (!cancelled) timer = setTimeout(poll, 2500);
    };
    timer = setTimeout(poll, 1200);
    return () => { cancelled = true; clearTimeout(timer); };
  }, [source?.id, source?.status]);
  useEffect(() => {
    if (!result?.project || ['completed', 'failed'].includes(result.project.status)) return;
    let cancelled = false, timer;
    const poll = async () => {
      try {
        const next = await manualApi.project(result.project.id);
        if (!cancelled) {
          setResult(next); setError('');
          if (['completed', 'failed'].includes(next.project.status)) { refresh().catch(() => {}); return; }
        }
      } catch (failure) { if (!cancelled) setError(manualError(failure)); }
      if (!cancelled) timer = setTimeout(poll, 2500);
    };
    timer = setTimeout(poll, 1000);
    return () => { cancelled = true; clearTimeout(timer); };
  }, [result?.project?.id, result?.project?.status, refresh]);
  const perform = async (label, action) => {
    if (operation.current) return null;
    operation.current = true; setBusy(label); setError('');
    try { return await action(); }
    catch (failure) { if (alive.current) setError(manualError(failure)); return null; }
    finally { operation.current = false; if (alive.current) setBusy(''); }
  };
  const choose = (value) => perform('source', async () => {
    const [kind, id] = value.split(':');
    const data = kind === 'project' ? await manualApi.addSource({ project_id: id }) : await manualApi.source(id);
    if (alive.current) { remember(data); setResult(null); setUpload(null); await refresh(); }
    return data;
  });
  const uploadFile = async (newFile) => {
    if (operation.current) return;
    const chosenFile = newFile || file.current;
    if (!chosenFile) return;
    if (!/\.(mp4|mov|m4v|webm|mkv|avi)$/i.test(chosenFile.name) || !chosenFile.size || chosenFile.size > MAX_VIDEO_BYTES) {
      setError('Choose an MP4, MOV, M4V, WebM, MKV or AVI video, up to 1 GB.'); return;
    }
    if (newFile) { file.current = newFile; session.current = { id: uuid() }; }
    controller.current = new AbortController();
    await perform('upload', async () => {
      try {
        const data = await uploadVideoInParts(chosenFile, { purpose: 'manual', clips_requested: 1,
          max_clip_seconds: 180, caption_enabled: false, face_track: false, trial_id: '' }, session.current,
          (progress) => { if (alive.current) setUpload((old) => ({ ...old, ...progress, filename: chosenFile.name })); },
          { deferProcessing: true, signal: controller.current.signal });
        if (!alive.current || controller.current.signal.aborted) return;
        setUpload((old) => ({ ...old, phase: 'Video saved · preparing manual playback' }));
        const added = await manualApi.addSource({ upload_id: data.upload_id });
        if (alive.current) { remember(added); setResult(null); file.current = null; setUpload(null); await refresh(); }
      } catch (failure) {
        if (alive.current) setError(failure.code === 'ERR_CANCELED' ? 'Upload paused. Resume to continue from saved parts.' : uploadErrorMessage(failure));
      }
    });
  };
  const retryPrepare = () => perform('source', async () => {
    const data = await manualApi.prepare(source.id); if (alive.current) setSource(data); return data;
  });
  const exportClips = (rows, requestId) => perform('export', async () => {
    const data = await manualApi.export({ request_id: requestId, source_id: source.id,
      clips: rows.map(({ title, start, end }) => ({ title, start, end })) });
    if (alive.current) { setResult(data); await refresh(); }
    return data;
  });
  const openExport = (id) => perform('history', async () => {
    const data = await manualApi.project(id); if (alive.current) setResult(data); return data;
  });
  return { library, source, exports, result, busy, error, upload, refresh, choose, uploadFile, retryPrepare,
    exportClips, openExport, canResume: Boolean(file.current), pauseUpload: () => controller.current?.abort(),
    clearError: () => setError(''), clearResult: () => setResult(null) };
}
